/** 변동 > 문구 효과 — what followed a change of name or subtitle. The server finds
 *  the changes in the listing history by itself and measures each store at seven
 *  and fourteen days, so the console only shows what has come in. */

import { useState } from 'react'
import { ChevronRight, PenLine } from 'lucide-react'
import type { ListingEffect } from '@/api/client'
import { useListingEffects } from '@/lib/queries'
import { cn } from '@/lib/utils'
import { AppIcon, CountryTag, DataTable, EmptyState, type Column } from '@/ui'
import { useIsWide } from '@/ui/useMediaQuery'

const keyOf = (e: ListingEffect) => `${e.app_id}/${e.locale}/${e.country}/${e.date}/${e.days}`

function percent(after: number, before: number): string {
  if (before <= 0) return '—'
  const p = Math.round(((after - before) / before) * 100)
  return `${p > 0 ? '+' : ''}${p}%`
}

const rankText = (r: number) => (r > 0 ? `#${r}` : '순위 밖')

function Change({ effect: e, className }: { effect: ListingEffect; className?: string }) {
  const text = e.old_name !== e.new_name ? `${e.old_name} → ${e.new_name}` : `${e.old_subtitle} → ${e.new_subtitle}`
  return (
    <span className={cn('block min-w-0 truncate', className)} title={text}>
      {text}
    </span>
  )
}

function Ranks({ effect: e }: { effect: ListingEffect }) {
  return (
    <span className="inline-flex items-baseline gap-x-2 whitespace-nowrap tabular-nums">
      <span className="text-delta-up">
        <span aria-hidden>▲</span>
        <span className="sr-only">좋아짐 </span>
        {e.better}
      </span>
      <span className="text-delta-down">
        <span aria-hidden>▼</span>
        <span className="sr-only">나빠짐 </span>
        {e.worse}
      </span>
      <span className="text-muted-foreground">그대로 {e.same}</span>
      {e.unknown > 0 && <span className="text-muted-foreground">모름 {e.unknown}</span>}
    </span>
  )
}

export default function Effects() {
  const effects = useListingEffects()
  // Six columns do not fit beside the sidebar at tablet width.
  const wide = useIsWide()
  const [openKeys, setOpenKeys] = useState<Set<string>>(new Set())
  const toggle = (key: string) =>
    setOpenKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  const columns: Column<ListingEffect>[] = [
    {
      key: 'app',
      header: '앱 · 바뀐 문구',
      card: 'title',
      cell: (e) => (
        <span className="block min-w-0">
          <span className="flex min-w-0 items-center gap-2">
            <AppIcon src={e.icon} name={e.app} size={20} />
            <span className="truncate font-medium">{e.app}</span>
            <CountryTag code={e.country} />
          </span>
          {wide && <Change effect={e} className="mt-0.5 max-w-[12rem] text-meta text-muted-foreground xl:max-w-[26rem]" />}
        </span>
      ),
    },
    { key: 'days', header: '시점', card: 'meta', className: 'whitespace-nowrap tabular-nums', cell: (e) => `${e.date.slice(5).replace('-', '/')} + ${e.days}일` },
    { key: 'change', header: '바뀐 문구', card: 'sub', cardOnly: true, cell: (e) => <Change effect={e} /> },
    { key: 'ranks', header: '문구 순위', card: 'line', cell: (e) => <Ranks effect={e} /> },
    {
      key: 'search',
      header: '검색 노출',
      align: 'right',
      card: 'line',
      className: 'tabular-nums',
      cell: (e) => (e.search ? percent(e.search.after, e.search.before) : <span className="text-muted-foreground">집계 전</span>),
    },
    {
      key: 'others',
      header: '안 바꾼 앱',
      align: 'right',
      card: 'line',
      className: 'tabular-nums text-muted-foreground',
      cell: (e) => (e.search ? percent(e.search.others_after, e.search.others_before) : '—'),
    },
    {
      key: 'expand',
      header: <span className="sr-only">펼치기</span>,
      width: '44px',
      card: 'actions',
      cell: (e) => {
        if (e.moves.length === 0) return null
        const key = keyOf(e)
        const on = openKeys.has(key)
        return (
          <button
            type="button"
            aria-expanded={on}
            aria-label={`${e.app} 문구별 순위 ${on ? '접기' : '펼치기'}`}
            onClick={(ev) => {
              ev.stopPropagation()
              toggle(key)
            }}
            onKeyDown={(ev) => ev.stopPropagation()}
            className="inline-flex h-8 items-center gap-1 rounded-md px-2 text-meta font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <ChevronRight size={16} aria-hidden className={cn('transition-transform', on && 'rotate-90')} />
            <span className="md:sr-only">문구별 순위</span>
          </button>
        )
      },
    },
  ]

  const rows = effects.data?.items
  return (
    <div className="space-y-3">
      {(rows?.length ?? 0) > 0 && (
        <p className="text-meta text-muted-foreground">
          바뀐 날 앞뒤로 같은 일수를 비교한다. 안 바꾼 앱은 같은 나라에서 그 기간에 이름과 부제를 그대로 둔 앱의 합계다. 비교일 뿐 원인을 증명하지 않는다.
        </p>
      )}
      <DataTable
        label="문구 변경 효과"
        columns={columns}
        rows={rows}
        rowKey={keyOf}
        mode={wide ? 'table' : 'cards'}
        loading={effects.isLoading}
        error={effects.error}
        onRetry={() => effects.refetch()}
        expand={{
          isOpen: (e) => openKeys.has(keyOf(e)),
          render: (e) => (
            <ul className="max-w-md space-y-1">
              {e.moves.map((m) => (
                <li key={m.term} className="flex items-baseline justify-between gap-4 text-table">
                  <span className="min-w-0 truncate">{m.term}</span>
                  <span className="shrink-0 tabular-nums text-muted-foreground">
                    {rankText(m.before)} → {rankText(m.after)}
                  </span>
                </li>
              ))}
            </ul>
          ),
        }}
        empty={
          <EmptyState
            icon={PenLine}
            title="아직 잴 변경이 없다"
            description="앱 이름이나 부제가 바뀌면 7일, 14일 뒤의 문구 순위와 검색 노출을 여기서 비교한다."
          />
        }
      />
    </div>
  )
}
